
import { Link } from "react-router-dom";
import { Calculator, ArrowDown, Book, History, Target, Network } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import Header from "@/components/shared/Header";

const Mathematics = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero Section */}
      <section className="py-20 px-6 bg-whiter from-primary/5 to-muted/20">
        <div className="container mx-auto text-center max-w-4xl">
          <div className="mx-auto w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mb-8">
            <Calculator className="w-10 h-10 text-primary" />
          </div>
          <h1 className="text-5xl font-bold mb-6 bg-white from-primary to-primary/70 bg-clip-text text-transparent">
            Mathematics
          </h1>
          <p className="text-xl text-black leading-relaxed mb-8">
            The profound language through which humans have sought to understand reality—from the ideal to the empirical, 
            from the simple to the infinite. Explore the unfolding logic, abstraction, and application of number, space, structure, and change.
          </p>
          <ArrowDown className="mx-auto w-6 h-6 text-black animate-bounce" />
        </div>
      </section>

      {/* Mathematics Domains */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="grid gap-8">
            
            {/* Foundational Fields */}
            <Card className="group hover:shadow-lg transition-all duration-300">
              <CardHeader>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <Book className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-2xl">Foundational Fields</CardTitle>
                    <CardDescription className="text-lg">
                      The core pillars upon which all other branches of mathematics are constructed
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="font-semibold mb-3 text-primary">Basic Foundations</h4>
                    <ul className="space-y-2 text-black">
                      <li>• Arithmetic - Natural numbers and operations</li>
                      <li>• Algebra - Variables, equations, abstract structures</li>
                      <li>• Geometry - Space, form, and transformation</li>
                      <li>• Trigonometry - Angular relationships and waves</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3 text-primary">Advanced Foundations</h4>
                    <ul className="space-y-2 text-black">
                      <li>• Calculus - Limits, derivatives, and integrals</li>
                      <li>• Linear Algebra - Vectors and matrices</li>
                      <li>• Set Theory & Logic - Mathematical foundations</li>
                      <li>• Abstract Algebra - Groups, rings, and fields</li>
                    </ul>
                  </div>
                </div>
                <Button asChild>
                  <Link to="/mathematics/foundational-fields">Explore Foundational Fields</Link>
                </Button>
              </CardContent>
            </Card>

            {/* Applied Mathematics */}
            <Card className="group hover:shadow-lg transition-all duration-300">
              <CardHeader>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <Target className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-2xl">Applied Mathematics</CardTitle>
                    <CardDescription className="text-lg">
                      Mathematical tools for modeling real-world phenomena and solving practical problems
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="font-semibold mb-3 text-primary">Statistical & Computational</h4>
                    <ul className="space-y-2 text-black">
                      <li>• Statistics & Probability - Data analysis and uncertainty</li>
                      <li>• Numerical Methods - Computational algorithms</li>
                      <li>• Optimization - Decision theory and efficiency</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3 text-primary">Physical & Engineering</h4>
                    <ul className="space-y-2 text-black">
                      <li>• Mathematical Physics - Quantum and classical mechanics</li>
                      <li>• Differential Equations - Dynamic system modeling</li>
                      <li>• Computational Mathematics - Algorithms and data structures</li>
                    </ul>
                  </div>
                </div>
                <Button asChild>
                  <Link to="/mathematics/applied-mathematics">Explore Applied Mathematics</Link>
                </Button>
              </CardContent>
            </Card>

            {/* Historical Development */}
            <Card className="group hover:shadow-lg transition-all duration-300">
              <CardHeader>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <History className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-2xl">Mathematics Across Time</CardTitle>
                    <CardDescription className="text-lg">
                      The historical development of mathematical thought across cultures and eras
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="font-semibold mb-3 text-primary">Ancient & Classical</h4>
                    <ul className="space-y-2 text-black">
                      <li>• Ancient Mathematics - Babylonian, Egyptian, Greek</li>
                      <li>• Medieval & Islamic Contributions</li>
                      <li>• Renaissance & Enlightenment Advances</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3 text-primary">Modern Era</h4>
                    <ul className="space-y-2 text-black">
                      <li>• 19th Century Formalism & Foundations</li>
                      <li>• Rise of Modern Mathematics</li>
                      <li>• Galois, Hilbert, Gödel, Turing</li>
                    </ul>
                  </div>
                </div>
                <Button asChild>
                  <Link to="/mathematics/historical-development">Explore Mathematical History</Link>
                </Button>
              </CardContent>
            </Card>

            {/* Interdisciplinary Pathways */}
            <Card className="group hover:shadow-lg transition-all duration-300">
              <CardHeader>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <Network className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-2xl">Interdisciplinary Mathematics</CardTitle>
                    <CardDescription className="text-lg">
                      Mathematics' integration with philosophy, physics, engineering, and beyond
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="font-semibold mb-3 text-primary">Theoretical Connections</h4>
                    <ul className="space-y-2 text-black">
                      <li>• Mathematics & Philosophy - Logic and truth</li>
                      <li>• Mathematics & Physics - Symmetry and spacetime</li>
                      <li>• Mathematics & Computer Science - Algorithms and complexity</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3 text-primary">Applied Connections</h4>
                    <ul className="space-y-2 text-black">
                      <li>• Mathematics & Engineering - Structural analysis</li>
                      <li>• Mathematics & Economics - Game theory and modeling</li>
                      <li>• Mathematics & Biology - Population dynamics</li>
                    </ul>
                  </div>
                </div>
                <Button asChild>
                  <Link to="/mathematics/interdisciplinary-pathways">Explore Intersections</Link>
                </Button>
              </CardContent>
            </Card>

          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="py-16 px-6 bg-muted/30">
        <div className="container mx-auto text-center max-w-4xl">
          <h3 className="text-2xl font-bold mb-6">Continue Your Mathematical Journey</h3>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" variant="outline">
              <Link to="/human-inquiry">Back to Human Inquiry</Link>
            </Button>
            <Button asChild size="lg">
              <Link to="/convergence">Explore Convergence</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Mathematics;

